import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  CheckmarkSquare02Icon,
  File01Icon,
  Files01Icon,
  FolderIcon,
  Search01Icon,
  Settings05Icon,
  Task01Icon,
  UserGroupIcon,
  Cancel01Icon,
} from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"

import {
  type GlobalSearchResult,
  type SearchResultType,
  useDebouncedValue,
  useGlobalSearch,
} from "@/api/search/useGlobalSearch"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { CreateTaskDialog } from "@/components/create-task-dialog"
import { CreateProjectDialog } from "@/components/create-project-dialog"
import { cn } from "@/lib/utils"
import { useWorkspace } from "@/workspaces/WorkspaceContext"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const FILTERS: Array<{ value: "all" | SearchResultType; label: string; types: SearchResultType[] }> = [
  { value: "all", label: "Все", types: ["task", "project", "file", "doc", "person"] },
  { value: "task", label: "Задачи", types: ["task"] },
  { value: "project", label: "Проекты", types: ["project"] },
  { value: "file", label: "Файлы", types: ["file"] },
  { value: "doc", label: "Документы", types: ["doc"] },
  { value: "person", label: "Люди", types: ["person"] },
]

const TYPE_LABELS: Record<SearchResultType, string> = {
  task: "Задачи",
  project: "Проекты",
  file: "Файлы",
  doc: "Документы",
  person: "Люди",
}

const RECENT_LIMIT = 6

export function GlobalSearch({ open, onOpenChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const { path, workspace } = useWorkspace()
  const [query, setQuery] = useState("")
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["value"]>("all")
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recent, setRecent] = useState<GlobalSearchResult[]>([])
  const [createTaskOpen, setCreateTaskOpen] = useState(false)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const debouncedQuery = useDebouncedValue(query)
  const activeTypes = FILTERS.find((item) => item.value === filter)?.types ?? FILTERS[0].types
  const search = useGlobalSearch(debouncedQuery, activeTypes, 24)
  const results = search.data?.results ?? []
  const selected = results[selectedIndex] ?? results[0] ?? null

  const recentKey = `chrona:global-search:recent:${workspace.slug}`

  useEffect(() => {
    if (!open) return
    setTimeout(() => inputRef.current?.focus(), 0)
    try {
      setRecent(JSON.parse(window.localStorage.getItem(recentKey) || "[]"))
    } catch {
      setRecent([])
    }
  }, [open, recentKey])

  useEffect(() => {
    setSelectedIndex(0)
  }, [debouncedQuery, filter])

  const grouped = useMemo(() => {
    return results.reduce<Record<SearchResultType, GlobalSearchResult[]>>(
      (acc, item) => {
        acc[item.type].push(item)
        return acc
      },
      { task: [], project: [], file: [], doc: [], person: [] },
    )
  }, [results])

  function remember(item: GlobalSearchResult) {
    const next = [item, ...recent.filter((entry) => !(entry.type === item.type && entry.id === item.id))].slice(0, RECENT_LIMIT)
    setRecent(next)
    window.localStorage.setItem(recentKey, JSON.stringify(next))
  }

  function openResult(item: GlobalSearchResult, newTab = false) {
    remember(item)
    const url = path(item.url)
    onOpenChange(false)
    if (newTab) {
      window.open(url, "_blank", "noopener,noreferrer")
      return
    }
    navigate(url)
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setSelectedIndex((current) => Math.min(current + 1, Math.max(results.length - 1, 0)))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setSelectedIndex((current) => Math.max(current - 1, 0))
    } else if (event.key === "Tab") {
      event.preventDefault()
      const index = FILTERS.findIndex((item) => item.value === filter)
      setFilter(FILTERS[(index + 1) % FILTERS.length].value)
    } else if (event.key === "Enter" && selected) {
      event.preventDefault()
      openResult(selected, event.metaKey || event.ctrlKey)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="inset-0 top-0 left-0 flex h-[100dvh] max-h-none w-[100dvw] max-w-[100dvw] translate-x-0 translate-y-0 flex-col gap-0 overflow-x-hidden overflow-y-hidden rounded-none p-0 sm:max-w-[100dvw] md:left-1/2 md:top-[12vh] md:h-auto md:max-h-[78vh] md:w-full md:max-w-[920px] md:-translate-x-1/2 md:translate-y-0 md:rounded-lg"
      >
        <DialogTitle className="sr-only">Глобальный поиск</DialogTitle>
        <div className="border-b p-3">
          <div className="flex items-center gap-2 rounded-md border bg-background px-3">
            <HugeiconsIcon icon={Search01Icon} strokeWidth={2} className="size-4 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Поиск..."
              className="h-10 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 md:hidden"
              onClick={() => onOpenChange(false)}
              aria-label="Закрыть поиск"
            >
              <HugeiconsIcon icon={Cancel01Icon} strokeWidth={2} className="size-4" />
            </Button>
            <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground sm:inline">Esc</kbd>
          </div>
          <div className="mt-3 flex gap-1 overflow-x-auto">
            {FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={cn(
                  "h-7 shrink-0 rounded-md px-2.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                  filter === item.value && "bg-foreground text-background hover:bg-foreground hover:text-background",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {query.trim().length < 2 ? (
          <ZeroState
            recent={recent}
            onOpenResult={openResult}
            onNavigate={(url) => { onOpenChange(false); navigate(path(url)) }}
            onCreateTask={() => { onOpenChange(false); setCreateTaskOpen(true) }}
            onCreateProject={() => { onOpenChange(false); setCreateProjectOpen(true) }}
          />
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:min-h-[420px] lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-h-0 overflow-y-auto p-2">
              {search.isFetching ? <div className="px-3 py-2 text-xs text-muted-foreground">Поиск...</div> : null}
              {!search.isFetching && results.length === 0 ? (
                <div className="px-3 py-12 text-center text-sm text-muted-foreground">Ничего не найдено</div>
              ) : null}
              {FILTERS.slice(1).map((filterItem) => {
                const items = grouped[filterItem.value as SearchResultType]
                if (!items.length) return null
                return (
                  <div key={filterItem.value} className="mb-2">
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">{TYPE_LABELS[filterItem.value as SearchResultType]}</div>
                    {items.map((item) => {
                      const flatIndex = results.findIndex((candidate) => candidate.type === item.type && candidate.id === item.id)
                      return (
                        <ResultRow
                          key={`${item.type}:${item.id}`}
                          item={item}
                          active={flatIndex === selectedIndex}
                          query={debouncedQuery}
                          onMouseEnter={() => setSelectedIndex(flatIndex)}
                          onClick={() => openResult(item)}
                        />
                      )
                    })}
                  </div>
                )
              })}
            </div>
            <Preview item={selected} query={debouncedQuery} />
          </div>
        )}
      </DialogContent>
    </Dialog>

    <CreateTaskDialog open={createTaskOpen} onClose={() => setCreateTaskOpen(false)} />
    <CreateProjectDialog open={createProjectOpen} onClose={() => setCreateProjectOpen(false)} />
    </>
  )
}

export function useGlobalSearchHotkey(open: boolean, setOpen: (open: boolean) => void) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "k" || (!event.metaKey && !event.ctrlKey)) return
      const target = event.target as HTMLElement | null
      const tag = target?.tagName?.toLowerCase()
      const editable = tag === "input" || tag === "textarea" || target?.isContentEditable
      if (editable && !target?.closest("[data-slot='dialog-content']")) return
      event.preventDefault()
      setOpen(!open)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, setOpen])
}

function ResultRow({ item, active, query, onMouseEnter, onClick }: {
  item: GlobalSearchResult
  active: boolean
  query: string
  onMouseEnter: () => void
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn("flex w-full items-center gap-3 rounded-md px-2 py-2 text-left", active ? "bg-muted text-foreground" : "hover:bg-muted/70")}
    >
      <ResultIcon type={item.type} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{item.title}</span>
        <span className="block truncate text-xs text-muted-foreground">{item.snippet || item.subtitle || TYPE_LABELS[item.type]}</span>
      </span>
      {item.snippet ? <span className="sr-only">{highlight(item.snippet, query)}</span> : null}
    </button>
  )
}

function Preview({ item, query }: { item: GlobalSearchResult | null; query: string }) {
  if (!item) {
    return <aside className="hidden border-l bg-muted/20 p-4 text-sm text-muted-foreground lg:block">Выберите результат</aside>
  }
  return (
    <aside className="hidden border-l bg-muted/20 p-4 lg:block">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <ResultIcon type={item.type} />
        {TYPE_LABELS[item.type]}
      </div>
      <h3 className="mt-3 break-words text-base font-semibold">{item.title}</h3>
      {item.subtitle ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.subtitle}</p> : null}
      {item.snippet ? <p className="mt-4 text-sm leading-6">{highlight(item.snippet, query)}</p> : null}
      <div className="mt-5 rounded-md border bg-background px-3 py-2 text-xs text-muted-foreground">Enter - открыть, Cmd/Ctrl+Enter - новая вкладка</div>
    </aside>
  )
}

function ZeroState({ recent, onOpenResult, onNavigate, onCreateTask, onCreateProject }: {
  recent: GlobalSearchResult[]
  onOpenResult: (item: GlobalSearchResult) => void
  onNavigate: (url: string) => void
  onCreateTask: () => void
  onCreateProject: () => void
}) {
  return (
    <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 md:min-h-[360px] md:grid-cols-2">
      <div>
        <div className="mb-2 text-xs font-semibold text-muted-foreground">Недавно просмотрено</div>
        {recent.length ? recent.map((item) => (
          <ResultRow key={`${item.type}:${item.id}`} item={item} active={false} query="" onMouseEnter={() => {}} onClick={() => onOpenResult(item)} />
        )) : <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">Пока пусто</div>}
      </div>
      <div>
        <div className="mb-2 text-xs font-semibold text-muted-foreground">Быстрые действия</div>
        <QuickAction icon={CheckmarkSquare02Icon} label="Создать задачу" onClick={onCreateTask} />
        <QuickAction icon={FolderIcon} label="Создать проект" onClick={onCreateProject} />
        <QuickAction icon={Settings05Icon} label="Перейти в настройки" onClick={() => onNavigate("/settings/general")} />
      </div>
    </div>
  )
}

function QuickAction({ icon, label, onClick }: { icon: typeof Search01Icon; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted">
      <HugeiconsIcon icon={icon} strokeWidth={2} className="size-4 text-muted-foreground" />
      {label}
    </button>
  )
}

function ResultIcon({ type }: { type: SearchResultType }) {
  const icon = {
    task: Task01Icon,
    project: FolderIcon,
    file: Files01Icon,
    doc: File01Icon,
    person: UserGroupIcon,
  }[type]
  return <HugeiconsIcon icon={icon} strokeWidth={2} className="size-4 shrink-0 text-muted-foreground" />
}

function highlight(text: string, query: string) {
  if (!query.trim()) return text
  const index = text.toLowerCase().indexOf(query.trim().toLowerCase())
  if (index < 0) return text
  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded bg-yellow-200 px-0.5 text-foreground">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  )
}
